import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { useTourAllowance } from '../lib/entitlements'
import { useTours } from '../lib/tours'
import { useShowsForTours } from '../lib/firestore'
import { formatDateRange, formatDayLong } from '../lib/format'
import type { Show, Tour, WithId } from '../types/models'
import Page from '../components/Page'
import Poster from '../components/Poster'
import Empty from '../components/Empty'
import Icon from '../components/Icon'
import TourGrid from '../components/TourGrid'

/* ============================================================================
   Tours.

   A dashboard, not a list. What is happening now gets the whole top of the
   page with the numbers that matter on it; what is coming next is a short row;
   what already happened is a shelf you flick through.

   The old version stacked three identical lists, which meant a tour running
   tonight looked exactly like one from 2019. After a backfill that is dozens
   of rows deep and the live tour is somewhere in the middle of it.
   ============================================================================ */

export default function Tours() {
  const { uid } = useAuth()
  const navigate = useNavigate()
  const { tours, owned, crewed, loading } = useTours(uid ?? undefined)
  const allowance = useTourAllowance(tours, uid ?? undefined)

  const todayMs = useMemo(() => {
    const midnight = new Date()
    midnight.setHours(0, 0, 0, 0)
    return midnight.getTime()
  }, [])

  // Split from what you own. Tours somebody invited you onto are somebody
  // else's plan, and mixing them into "upcoming" makes your own book look
  // fuller than it is.
  const current = owned.filter(
    (tour) => tour.startDate.toMillis() <= todayMs && tour.endDate.toMillis() >= todayMs,
  )
  const upcoming = owned
    .filter((tour) => tour.startDate.toMillis() > todayMs)
    .sort((a, b) => a.startDate.toMillis() - b.startDate.toMillis())
  const past = owned
    .filter((tour) => tour.endDate.toMillis() < todayMs)
    .sort((a, b) => b.startDate.toMillis() - a.startDate.toMillis())
  const shared = crewed.sort((a, b) => b.startDate.toMillis() - a.startDate.toMillis())

  // Running now if there is one, otherwise the next one booked. Nobody opens
  // this page to find out about 2019.
  const headline = current[0] ?? upcoming[0] ?? null
  const rest = upcoming.filter((tour) => tour.id !== headline?.id)

  return (
    <Page
      title="Tours"
      actions={
        <button
          className="btn btn-primary"
          disabled={!allowance.allowed}
          // The button stays visible when the plan is full rather than
          // vanishing — a control that disappears reads as a bug, and the
          // note below says exactly why it can't be pressed.
          title={allowance.allowed ? undefined : allowance.message}
          onClick={() => navigate('/tours/new')}
        >
          New tour
        </button>
      }
    >
      {!allowance.allowed && (
        <p className="panel panel-pad t-caption" style={{ borderColor: 'var(--accent)' }}>
          {allowance.message} Everything you've already made stays readable and exportable.
        </p>
      )}

      {loading && tours.length === 0 && <p className="t-caption">Loading…</p>}

      {!loading && tours.length === 0 && (
        <Empty
          title="No tours yet"
          message="Every show, flight, hotel and call sheet hangs off a tour. Start with one."
          action={
            allowance.allowed
              ? { label: 'Create your first tour', onClick: () => navigate('/tours/new') }
              : undefined
          }
        />
      )}

      {headline && (
        <HeadlineTour tour={headline} live={current.length > 0} />
      )}

      {rest.length > 0 && (
        <section className="section">
          <h2 className="t-eyebrow">Upcoming tours</h2>
          <div className="stack">
            {rest.map((tour) => (
              <TourRow key={tour.id} tour={tour} />
            ))}
          </div>
        </section>
      )}

      {past.length > 0 && (
        <section className="section">
          <div className="spread" style={{ alignItems: 'baseline' }}>
            <h2 className="t-eyebrow">Past tours</h2>
            <span className="t-caption">{past.length}</span>
          </div>
          <TourGrid tours={past} />
        </section>
      )}

      {shared.length > 0 && (
        <section className="section">
          <div className="spread" style={{ alignItems: 'baseline' }}>
            <h2 className="t-eyebrow">Shared with me</h2>
            <span className="t-caption">{shared.length}</span>
          </div>
          <TourGrid tours={shared} />
        </section>
      )}
    </Page>
  )
}

/* ------------------------------------------------------------- headline ---- */

/**
 * The tour that is happening, given the room to say something.
 *
 * Its numbers are read from its shows rather than stored, so they're right
 * whether the tour was typed in, imported from a spreadsheet or backfilled.
 */
function HeadlineTour({ tour, live }: { tour: WithId<Tour>; live: boolean }) {
  const shows = useShowsForTours<Show>(useMemo(() => [tour.id], [tour.id]))

  const sorted = useMemo(
    () => [...shows.data].sort((a, b) => a.date.toMillis() - b.date.toMillis()),
    [shows.data],
  )

  const now = Date.now()
  const next = sorted.find((show) => show.date.toMillis() >= now)
  const played = sorted.filter((show) => show.date.toMillis() < now).length
  const cities = new Set(sorted.map((show) => show.city).filter(Boolean)).size

  const days = Math.max(
    0,
    Math.ceil((tour.startDate.toMillis() - now) / 86_400_000),
  )

  return (
    <section
      className="panel"
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        gap: 22,
        padding: 22,
        alignItems: 'stretch',
        borderColor: live ? 'var(--accent)' : undefined,
      }}
    >
      {/* Fills the card's height, so the artwork is inset by the padding at
          the top and the bottom equally rather than stopping short.
          Wider than it was: at 150 the artwork read as a spine rather than a
          poster, and this is the one place on the page where the tour's
          identity is meant to carry. */}
      <Link
        to={`/tours/${tour.id}`}
        style={{ display: 'block', alignSelf: 'stretch', width: 210, flex: 'none' }}
      >
        <Poster url={tour.posterURL} width={210} alt={tour.tourName} fill />
      </Link>

      <div className="stack" style={{ gap: 16, minWidth: 0 }}>
        <div className="stack" style={{ gap: 4, minWidth: 0 }}>
          <span className="t-eyebrow" style={{ color: live ? 'var(--accent-text)' : undefined }}>
            {live ? 'On the road' : 'Next tour'} · {tour.artist}
          </span>
          <Link to={`/tours/${tour.id}`} className="t-display-l truncate">
            {tour.tourName}
          </Link>
          <span className="t-spec-caption dim">
            {formatDateRange(tour.startDate, tour.endDate)}
          </span>
        </div>

        <div className="row" style={{ gap: 26, flexWrap: 'wrap' }}>
          <Stat value={String(sorted.length)} label={sorted.length === 1 ? 'Show' : 'Shows'} />
          {played > 0 && <Stat value={String(played)} label="Played" />}
          <Stat value={String(cities)} label={cities === 1 ? 'City' : 'Cities'} />
          {!live && <Stat value={String(days)} label="Days until" accent />}
        </div>

        {next && (
          <Link
            to={`/tours/${tour.id}/shows/${next.id}`}
            className="row panel"
            style={{ gap: 12, padding: '11px 13px', alignItems: 'center' }}
          >
            <span className="t-spec-caption faint" style={{ minWidth: 92 }}>
              {formatDayLong(next.date, next.timezone)}
            </span>
            <span className="grow stack" style={{ gap: 1, minWidth: 0 }}>
              <span className="truncate" style={{ fontWeight: 600 }}>
                {next.city}
              </span>
              <span className="t-caption truncate">{next.venueName}</span>
            </span>
            <Icon name="chevronRight" size={15} style={{ color: 'var(--text-tertiary)' }} />
          </Link>
        )}

        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <Link className="btn btn-secondary" to={`/tours/${tour.id}/schedule`}>
            Schedule
          </Link>
          <Link className="btn btn-secondary" to={`/tours/${tour.id}/travel`}>
            Travel
          </Link>
          <Link className="btn btn-secondary" to={`/tours/${tour.id}/crew`}>
            Crew
          </Link>
        </div>
      </div>
    </section>
  )
}

function Stat({ value, label, accent }: { value: string; label: string; accent?: boolean }) {
  return (
    <div className="stack" style={{ gap: 1 }}>
      <span className="t-display-s" style={{ color: accent ? 'var(--accent-text)' : undefined }}>
        {value}
      </span>
      <span className="t-spec-caption faint">{label}</span>
    </div>
  )
}

/* -------------------------------------------------------------------------- */

function TourRow({ tour }: { tour: WithId<Tour> }) {
  return (
    <Link
      to={`/tours/${tour.id}`}
      className="panel"
      style={{ display: 'flex', gap: 16, padding: 16 }}
    >
      <Poster url={tour.posterURL} width={62} />
      <div className="stack" style={{ gap: 5, minWidth: 0, justifyContent: 'center' }}>
        <span className="t-eyebrow">{tour.artist}</span>
        <span className="t-display-s truncate">{tour.tourName}</span>
        <span className="t-spec-caption dim">
          {formatDateRange(tour.startDate, tour.endDate)}
        </span>
      </div>
    </Link>
  )
}
